import React from 'react';
import { Package, MapPin, Calendar, DollarSign } from 'lucide-react';

const TransactionDetails = ({ payment, order, paymentService }) => {
  if (!payment || !order) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DollarSign className="h-5 w-5 mr-2 text-green-600" />
          Payment Summary
        </h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Amount Paid:</span>
            <span className="font-medium text-lg">
              {paymentService?.formatCurrency(payment?.amount)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Currency:</span>
            <span className="font-medium">{payment?.currency}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Payment Method:</span>
            <span className="font-medium capitalize">
              {payment?.payment_gateway?.replace('_', ' ')}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Status:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              paymentService?.getPaymentStatusColor(payment?.status)
            }`}>
              {payment?.status?.charAt(0)?.toUpperCase() + payment?.status?.slice(1)}
            </span>
          </div>
          
          {payment?.gateway_reference && (
            <div className="flex justify-between">
              <span className="text-gray-600">Reference:</span>
              <span className="font-mono text-sm">{payment?.gateway_reference}</span>
            </div>
          )}
        </div>
      </div>
      {/* Order Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Package className="h-5 w-5 mr-2 text-blue-600" />
          Order Details
        </h3>
        
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600">Order ID:</p>
            <p className="font-mono text-sm">{order?.id}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Customer:</p>
            <p className="font-medium">{order?.customer_name}</p>
            <p className="text-sm text-gray-500">{order?.customer_email}</p>
            {order?.customer_phone && (
              <p className="text-sm text-gray-500">{order?.customer_phone}</p>
            )}
          </div>

          {order?.service_address && (
            <div>
              <p className="text-sm text-gray-600 flex items-center mb-1">
                <MapPin className="h-4 w-4 mr-1" />
                Service Address:
              </p>
              <p className="text-sm">{order?.service_address}</p>
            </div>
          )}

          {order?.service_date && (
            <div>
              <p className="text-sm text-gray-600 flex items-center mb-1">
                <Calendar className="h-4 w-4 mr-1" />
                Scheduled Date:
              </p>
              <p className="text-sm">
                {new Date(order.service_date)?.toLocaleDateString('en-ZA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Order Items */}
      {order?.order_items && order?.order_items?.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Items Ordered</h3>
          
          <div className="space-y-3">
            {order?.order_items?.map((item) => (
              <div key={item?.id} className="flex justify-between items-start p-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {item?.services?.name || item?.products?.name}
                  </h4>
                  {item?.services?.category && (
                    <p className="text-xs text-gray-500 capitalize">
                      {item?.services?.category?.replace('_', ' ')}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Quantity: {item?.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {paymentService?.formatCurrency(item?.total_price)}
                  </p>
                  {item?.quantity > 1 && (
                    <p className="text-xs text-gray-500">
                      {paymentService?.formatCurrency(item?.unit_price)} each
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Transaction Timeline */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Payment Created:</span>
            <span>
              {new Date(payment.created_at)?.toLocaleDateString('en-ZA', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          
          {payment?.updated_at !== payment?.created_at && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Last Updated:</span>
              <span>
                {new Date(payment.updated_at)?.toLocaleDateString('en-ZA', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
          
          {payment?.processed_at && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Processed:</span>
              <span>
                {new Date(payment.processed_at)?.toLocaleDateString('en-ZA', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Gateway Response */}
      {payment?.gateway_response && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gateway Information</h3>
          
          <div className="text-sm space-y-2">
            {payment?.gateway_response?.transaction_id && (
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-mono text-xs">
                  {payment?.gateway_response?.transaction_id}
                </span>
              </div>
            )}
            
            {payment?.gateway_response?.reference && (
              <div className="flex justify-between">
                <span className="text-gray-600">Gateway Reference:</span>
                <span className="font-mono text-xs">
                  {payment?.gateway_response?.reference}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Support Contact */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Need Help?</h4>
        <p className="text-sm text-blue-700">
          If you have questions about this payment or need assistance, 
          our customer support team is here to help.
        </p>
        <div className="mt-2 text-sm text-blue-700">
          <p>Email: support@clean360.co.za</p>
          <p>Phone: +27 11 123 4567</p>
          <p>Hours: Mon-Fri 8:00-17:00 SAST</p>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;